import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { t } from 'i18next';
import { useTheme } from 'styled-components';
import { selectMenuZIndex } from '../../styles/global';

type Props = {
  value: any;
  onChange: (value: any) => void;
  disablePast?: boolean;
  disabled?: boolean;
  label?: string;
  minDateTime?: any;
};

export const BasicDateTimePicker = ({
  value,
  onChange,
  disablePast,
  disabled,
  label,
  minDateTime,
}: Props) => {
  const theme = useTheme();

  const pickerSurfaceStyles = {
    '& .MuiPaper-root': {
      backgroundColor: theme.colors.surface,
      color: theme.colors.onSurface,
      border: `1px solid ${theme.colors.onBackground}20`,
    },
    '& .MuiPickersLayout-root': {
      backgroundColor: theme.colors.surface,
      color: theme.colors.onSurface,
    },
    '& .MuiPickersToolbar-root': {
      backgroundColor: theme.colors.surface,
      color: theme.colors.onSurface,
      borderBottom: `1px solid ${theme.colors.onBackground}15`,
    },
    '& .MuiPickersToolbarText-root': {
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiPickersCalendarHeader-root': {
      color: theme.colors.onSurface,
    },
    '& .MuiPickersCalendarHeader-label': {
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiDayPicker-header': {
      color: theme.colors.onSurface,
    },
    '& .MuiDayPicker-weekDayLabel': {
      color: `${theme.colors.onSurface}B3 !important`,
    },
    '& .MuiIconButton-root': {
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiSvgIcon-root': {
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiTypography-root': {
      color: theme.colors.onSurface,
    },
    '& .MuiButtonBase-root': {
      color: theme.colors.onSurface,
    },
    '& .MuiPickersDay-root': {
      backgroundColor: 'transparent !important',
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiPickersDay-root:hover': {
      backgroundColor: `${theme.colors.onSurface}14 !important`,
    },
    '& .MuiPickersDay-root:focus': {
      backgroundColor: `${theme.colors.onSurface}14 !important`,
    },
    '& .MuiPickersDay-root.Mui-disabled': {
      backgroundColor: 'transparent !important',
      color: `${theme.colors.onSurface}66 !important`,
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: `${theme.colors.primary} !important`,
      color: `${theme.colors.onPrimary} !important`,
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: `${theme.colors.primary}DD !important`,
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
      backgroundColor: `${theme.colors.primary}DD !important`,
    },
    '& .MuiPickersDay-today': {
      borderColor: `${theme.colors.primary} !important`,
    },
    '& .MuiClock-pin, & .MuiClockPointer-root': {
      backgroundColor: `${theme.colors.primary} !important`,
    },
    '& .MuiClockPointer-thumb': {
      borderColor: `${theme.colors.primary} !important`,
      backgroundColor: `${theme.colors.primary} !important`,
    },
    '& .MuiClockNumber-root': {
      color: `${theme.colors.onSurface} !important`,
    },
    '& .MuiClockNumber-root.Mui-selected': {
      color: `${theme.colors.onPrimary} !important`,
      backgroundColor: `${theme.colors.primary} !important`,
    },
    '& .MuiDialogActions-root, & .MuiPickersActionBar-root': {
      backgroundColor: theme.colors.surface,
      borderTop: `1px solid ${theme.colors.onBackground}15`,
    },
    '& .MuiDialogActions-root .MuiButton-root, & .MuiPickersActionBar-root .MuiButton-root': {
      color: `${theme.colors.primary} !important`,
    },
    // Improve contrast for AM/PM toggle buttons in the time picker
    '& .MuiToggleButton-root': {
      color: `${theme.colors.onSurface} !important`,
      backgroundColor: 'transparent !important',
    },
    '& .MuiToggleButton-root.Mui-selected': {
      color: `${theme.colors.onPrimary} !important`,
      backgroundColor: `${theme.colors.primary} !important`,
    },
    '& .MuiPickersToolbar-ampmSelection, & .MuiPickersToolbar-ampmSelection *': {
      color: `${theme.colors.onSurface} !important`,
    },

    '& .MuiTypography-root, & .MuiTypography-caption *': {
      color: `${theme.colors.onSurface} !important`,
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        label={label || t('selectDate') as string}
        inputFormat="DD/MM/YYYY HH:mm"
        disablePast={disablePast}
        minDateTime={minDateTime}
        value={value}
        onChange={(newValue) => {
          onChange(newValue);
        }}
        PopperProps={{
          sx: {
            zIndex: selectMenuZIndex,
            ...pickerSurfaceStyles,
          },
        }}
        DialogProps={{
          sx: {
            zIndex: selectMenuZIndex,
            ...pickerSurfaceStyles,
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{
              '& .MuiInputBase-root': {
                color: theme.colors.text,
                backgroundColor: theme.colors.surface,
              },
              '& .MuiInputBase-input': {
                color: `${theme.colors.text} !important`,
                WebkitTextFillColor: `${theme.colors.text} !important`,
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: `${theme.colors.text}B3 !important`,
              },
              '& .MuiInputLabel-root': {
                color: theme.colors.text,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.colors.primary,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.colors.text,
                },
                '&:hover fieldset': {
                  borderColor: theme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.colors.primary,
                },
                '&.Mui-disabled fieldset': {
                  borderColor: `${theme.colors.text}40`,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.text,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.primary,
              },
              '& .MuiSvgIcon-root': {
                color: theme.colors.text,
              },
            }}
          />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};

BasicDateTimePicker.defaultProps = {
  disablePast: true,
  disabled: false,
};

export default BasicDateTimePicker;
